import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import ConfirmModal from "../../components/common/ConfirmModal";
import {
  getAllPlans,
  createPlan,
  updatePlan,
  deletePlan,
  toggleVisible,
  type SubscriptionPlan,
  type CreatePlanPayload,
} from "../../services/subscriptionService";
import { CreditCard, Pencil, Trash2 } from "lucide-react";

const DURATION_TYPES = ["days", "weeks", "months", "years"];

const emptyForm: CreatePlanPayload = {
  planName: "",
  price: 0,
  currency: "INR",
  description: "",
  durationType: "months",
  durationValue: 1,
  includedFeatures: [],
  planTag: "",
  isVisible: true,
  isActive: true,
};

const AdminSubscriptionPage = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [formData, setFormData] = useState<CreatePlanPayload>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingPlan, setDeletingPlan] = useState<SubscriptionPlan | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [featuresText, setFeaturesText] = useState("");

  const loadPlans = async () => {
    setLoading(true);
    try {
      const data = await getAllPlans();
      setPlans(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Failed to load subscription plans");
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
  }, []);

  const openCreate = () => {
    setEditingPlan(null);
    setFormData(emptyForm);
    setFeaturesText("");
    setShowFormModal(true);
  };

  const openEdit = (plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    setFormData({
      planName: plan.planName,
      price: plan.price,
      currency: plan.currency || "INR",
      description: plan.description || "",
      durationType: plan.durationType || "months",
      durationValue: plan.durationValue ?? 1,
      includedFeatures: plan.includedFeatures?.length ? plan.includedFeatures : [],
      planTag: plan.planTag || "",
      isVisible: plan.isVisible,
      isActive: plan.isActive,
    });
    setFeaturesText((plan.includedFeatures || []).join("\n"));
    setShowFormModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const includedFeatures = featuresText
      .split(/[\n,]/)
      .map((f) => f.trim())
      .filter(Boolean);

    if (!includedFeatures.length) {
      toast.error("Please add at least one feature");
      return;
    }

    const form = formData;
    if (!form.planName?.trim()) {
      toast.error("Plan name is required");
      return;
    }
    if (form.price === undefined || form.price === null || Number(form.price) < 0) {
      toast.error("Price is required");
      return;
    }
    if (!form.currency?.trim()) {
      toast.error("Currency is required");
      return;
    }
    if (!form.description?.trim()) {
      toast.error("Description is required");
      return;
    }
    if (form.durationValue === undefined || form.durationValue === null || Number(form.durationValue) < 1) {
      toast.error("Duration value is required");
      return;
    }
    if (!form.durationType?.trim()) {
      toast.error("Duration type is required");
      return;
    }
    if (!form.planTag?.trim()) {
      toast.error("Plan tag is required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        planName: form.planName.trim(),
        price: Number(form.price),
        currency: form.currency.trim() || "INR",
        description: form.description.trim() || "",
        durationType: form.durationType,
        durationValue: Number(form.durationValue),
        includedFeatures,
        planTag: form.planTag.trim() || null,
        isVisible: !!form.isVisible,
        isActive: true,
      };
      if (editingPlan) {
        await updatePlan(editingPlan.id, payload);
        toast.success("Plan updated successfully");
      } else {
        console.log("Create Subscription Payload:", payload);
        await createPlan(payload);
        toast.success("Plan created successfully");
      }
      setShowFormModal(false);
      loadPlans();
    } catch (err: any) {
      const msg = err?.response?.data?.message || "Operation failed";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (plan: SubscriptionPlan) => {
    setDeletingPlan(plan);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingPlan) return;
    setDeleting(true);
    try {
      await deletePlan(deletingPlan.id);
      toast.success("Plan deleted");
      setShowDeleteModal(false);
      setDeletingPlan(null);
      loadPlans();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleVisible = async (plan: SubscriptionPlan) => {
    setTogglingId(plan.id);
    try {
      await toggleVisible(plan.id);
      toast.success(plan.isVisible ? "Plan hidden" : "Plan visible");
      loadPlans();
    } catch (err: any) {
      const msg = err?.response?.data?.message || "Toggle failed";
      toast.error(msg);
    } finally {
      setTogglingId(null);
    }
  };

  const handleFormChange = (field: keyof CreatePlanPayload, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const parsedFeatures = featuresText
    .split(/[\n,]/)
    .map((f) => f.trim())
    .filter(Boolean);
  const isFormValid =
    !!formData.planName?.trim() &&
    formData.price !== undefined &&
    formData.price !== null &&
    Number(formData.price) >= 0 &&
    !!formData.currency?.trim() &&
    !!formData.description?.trim() &&
    formData.durationValue !== undefined &&
    formData.durationValue !== null &&
    Number(formData.durationValue) >= 1 &&
    !!formData.durationType?.trim() &&
    !!formData.planTag?.trim() &&
    parsedFeatures.length > 0;

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Subscription Plans</h1>
          <p className="text-gray-500 mt-1">Manage subscription plans and visibility.</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
        >
          <CreditCard size={18} />
          Add Subscription
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4" />
              <p className="text-gray-500 animate-pulse">Loading plans...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Plan</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Price</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Duration</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Tag</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Visible</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {plans.map((plan) => (
                    <tr key={plan.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <p className="text-sm font-semibold text-gray-900">{plan.planName}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {plan.price} {plan.currency}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {plan.durationValue} {plan.durationType}
                      </td>
                      <td className="px-6 py-4">
                        {plan.planTag ? (
                          <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                            {plan.planTag}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          type="button"
                          onClick={() => handleToggleVisible(plan)}
                          disabled={togglingId === plan.id}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                            plan.isVisible ? "bg-primary-600" : "bg-gray-200"
                          } ${togglingId === plan.id ? "opacity-50" : ""}`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                              plan.isVisible ? "translate-x-5" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openEdit(plan)}
                            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-primary-600 transition-colors"
                            title="Edit"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteClick(plan)}
                            className="p-2 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {plans.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                        No subscription plans yet. Add one to get started.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Form Modal (Create / Edit) */}
      {showFormModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingPlan ? "Edit Plan" : "Add Subscription Plan"}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Plan Name</label>
                <input
                  type="text"
                  required
                  value={formData.planName}
                  onChange={(e) => handleFormChange("planName", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Price</label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    required
                    value={formData.price || ""}
                    onChange={(e) => handleFormChange("price", Number(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Currency</label>
                  <input
                    type="text"
                    required
                    value={formData.currency}
                    onChange={(e) => handleFormChange("currency", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Description</label>
                <input
                  type="text"
                  required
                  value={formData.description || ""}
                  onChange={(e) => handleFormChange("description", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Duration Value</label>
                  <input
                    type="number"
                    min={1}
                    required
                    value={formData.durationValue ?? ""}
                    onChange={(e) => handleFormChange("durationValue", Number(e.target.value) || 1)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Duration Type</label>
                  <select
                    value={formData.durationType}
                    onChange={(e) => handleFormChange("durationType", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                  >
                    {DURATION_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Plan Tag (e.g. Most Popular)</label>
                <input
                  type="text"
                  required
                  value={formData.planTag || ""}
                  onChange={(e) => handleFormChange("planTag", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Included Features (required)</label>
                <textarea
                  required
                  value={featuresText}
                  onChange={(e) => setFeaturesText(e.target.value)}
                  placeholder="One per line or comma-separated"
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500 resize-y"
                />
              </div>
              {/* <div className="flex gap-2 pt-2">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.isVisible !== false}
                    onChange={(e) => handleFormChange("isVisible", e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Visible
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.isActive !== false}
                    onChange={(e) => handleFormChange("isActive", e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Active
                </label>
              </div> */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowFormModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || !isFormValid}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {saving ? "Saving..." : editingPlan ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showDeleteModal}
        title="Delete Plan"
        message={deletingPlan ? `Are you sure you want to delete "${deletingPlan.planName}"?` : ""}
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteModal(false);
          setDeletingPlan(null);
        }}
      />
    </div>
  );
};

export default AdminSubscriptionPage;
